import React, { useEffect, useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  TrendingUp,
  Building2,
  RefreshCw,
  AlertCircle,
  Calendar,
  ChevronDown,
  Activity,
  Layers,
  Cpu,
  CheckCircle2,
  Eye,
  EyeOff
} from 'lucide-react';
import apiService from '../services/apiService';
import { formatCurrency } from '../utils/formatters';

const BATCH_SIZE = 16;

const CustomForecastTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 backdrop-blur-md p-3.5 rounded-xl border border-slate-700 shadow-2xl text-xs space-y-2 z-50 text-white min-w-[200px]">
        <div className="font-bold text-slate-200 border-b border-slate-800 pb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-primary-400">
            <Calendar className="w-3.5 h-3.5" />
            <span>{label}</span>
          </div>
          <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
            Daily
          </span>
        </div>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => {
            const isPred = entry.dataKey && entry.dataKey.includes('_pred');
            return (
              <div key={`item-${index}`} className="flex items-center justify-between gap-4">
                <span className="flex items-center gap-1.5 text-slate-300 font-medium">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: entry.color || entry.stroke || '#3b82f6' }}
                  />
                  {isPred ? 'Forecast Model' : 'Historical Actual'}:
                </span>
                <span className="font-bold font-mono text-white">
                  {typeof entry.value === 'number' ? formatCurrency(entry.value) : entry.value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const ForecastingView: React.FC = () => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [showForecast, setShowForecast] = useState<boolean>(true);

  const loadProviders = async () => {
    try {
      const data = await apiService.getProvidersData(50);
      setProviders(data.map((p: any) => p.name));
    } catch (err) {
      console.error('Failed to load providers', err);
    }
  };

  const loadResults = async (providerName?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getForecastingResults(providerName || undefined);
      setResults(data);
    } catch (err: any) {
      if (err.message && err.message.includes('404')) {
        setError('No trained forecasting weights found for this provider filter. Click "Train ML Models" to generate predictions.');
      } else {
        setError(err.message || 'Failed to load forecasting results');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTrain = async () => {
    try {
      setLoading(true);
      setError(null);
      await apiService.trainForecasting(selectedProvider || undefined);
      await loadResults(selectedProvider || undefined);
    } catch (err: any) {
      setError(err.message || 'Model training pipeline encounter an issue');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setSelectedProvider(val);
    setError(null);
    loadResults(val || undefined);
  };

  useEffect(() => {
    loadProviders();
    loadResults(undefined);
  }, []);

  const chartData = useMemo(() => {
    if (!results) return [];
    const data: any[] = [];
    const { train, test, future } = results;

    train.dates.forEach((date: string, i: number) => {
      data.push({
        date,
        actual: train.values[i],
        type: 'train',
      });
    });

    test.dates.forEach((date: string, i: number) => {
      const entry: any = {
        date,
        actual: test.actual[i],
        type: 'test',
      };
      Object.keys(test.predictions).forEach(model => {
        entry[`${model}_pred`] = test.predictions[model][i];
      });
      data.push(entry);
    });

    future.dates.slice(0, BATCH_SIZE).forEach((date: string, i: number) => {
      const entry: any = {
        date,
        type: 'future',
      };
      Object.keys(future.predictions).forEach(model => {
        entry[`${model}_pred`] = future.predictions[model][i];
      });
      data.push(entry);
    });

    return data;
  }, [results]);

  const bestModel = results?.best_model || 'LSTM';
  const cutoffDate = results?.train?.dates?.[results.train.dates.length - 1];

  return (
    <div className="space-y-6 transition-all duration-300 ease-in-out">
      {/* Header with Glass Card & Cohesive Controls */}
      <div className="glass-panel p-5 sm:p-6 rounded-2xl border border-border shadow-sm flex flex-col lg:flex-row lg:items-center justify-between gap-4 transition-all duration-300">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 via-primary-600 to-indigo-700 text-white flex items-center justify-center shadow-lg shadow-primary-500/25 shrink-0 transition-transform duration-300 hover:scale-105">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl font-bold text-foreground truncate">Claims Cashflow Forecasting</h2>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200 shrink-0">
                <Cpu className="w-3 h-3" />
                Neural Sequence
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              Predict future net payable obligations across provider networks using LSTM & ML models
            </p>
          </div>
        </div>

        {/* Filter Controls Bar - Forced Flex Row */}
        <div className="flex flex-row items-center gap-2 sm:gap-2.5 shrink-0 flex-nowrap overflow-x-auto pb-1 lg:pb-0">
          {/* Provider Select Dropdown */}
          <div className="relative min-w-[160px] sm:min-w-[190px]">
            <Building2 className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors" />
            <select
              value={selectedProvider}
              onChange={handleProviderChange}
              className="w-full pl-8 sm:pl-9 pr-8 sm:pr-9 py-2 bg-card border border-border rounded-xl text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary-500 focus:outline-none transition-all duration-200 cursor-pointer appearance-none shadow-sm hover:border-primary-300 truncate"
            >
              <option value="">All Providers</option>
              {providers.map(p => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-muted-foreground absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* Toggle Projection Button */}
          <button
            type="button"
            onClick={() => setShowForecast(!showForecast)}
            className={`px-3 sm:px-3.5 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 sm:gap-2 border transition-all duration-200 cursor-pointer shrink-0 whitespace-nowrap active:scale-95 ${
              showForecast
                ? 'bg-primary-50 border-primary-300 text-primary-700 shadow-sm'
                : 'bg-card border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            {showForecast ? <Eye className="w-3.5 h-3.5 transition-transform" /> : <EyeOff className="w-3.5 h-3.5 transition-transform" />}
            <span>{showForecast ? 'Hide Projections' : 'Show Projections'}</span>
          </button>

          {/* Train ML Models CTA */}
          <button
            onClick={handleTrain}
            disabled={loading}
            className={`px-3.5 sm:px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 sm:gap-2 transition-all duration-200 cursor-pointer shrink-0 whitespace-nowrap active:scale-95 ${
              loading
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-primary-600 hover:from-blue-500 hover:to-primary-500 text-white shadow-md shadow-primary-600/25'
            }`}
          >
            {loading ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Cpu className="w-3.5 h-3.5" />
            )}
            <span>{loading ? 'Fitting...' : 'Train ML Models'}</span>
          </button>
        </div>
      </div>

      {error && !loading && (
        <div className="bg-amber-50/90 border border-amber-200 rounded-2xl p-4 sm:p-5 flex items-start sm:items-center gap-3.5 shadow-sm animate-fade-in transition-all">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1">
            <h4 className="text-xs font-bold text-amber-900">Forecasting Setup Required</h4>
            <p className="text-xs text-amber-800 mt-0.5">{error}</p>
          </div>
          <button
            onClick={handleTrain}
            className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shrink-0 transition"
          >
            Train Now
          </button>
        </div>
      )}

      {/* Initial Loading Skeleton when no results yet */}
      {loading && !results && (
        <div className="bg-card rounded-2xl border border-border p-12 text-center animate-pulse space-y-4">
          <div className="w-12 h-12 mx-auto rounded-xl bg-primary-100 dark:bg-primary-950/60 flex items-center justify-center text-primary-600">
            <RefreshCw className="w-6 h-6 animate-spin" />
          </div>
          <p className="text-sm font-bold text-foreground">Computing Cashflow Sequences...</p>
          <p className="text-xs text-muted-foreground">Running Neural LSTM weights across payment history</p>
        </div>
      )}

      {results && (
        <div className={`relative bg-card rounded-2xl border border-border shadow-sm p-5 sm:p-6 space-y-5 transition-all duration-300 ${loading ? 'opacity-70' : 'opacity-100'}`}>
          {/* Subtle Refresh Overlay when re-fetching with existing data */}
          {loading && (
            <div className="absolute inset-0 bg-background/40 backdrop-blur-[1px] rounded-2xl z-20 flex items-center justify-center transition-all duration-300">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900/90 text-white text-xs font-semibold shadow-2xl border border-slate-700">
                <RefreshCw className="w-3.5 h-3.5 animate-spin text-primary-400" />
                <span>Updating Projections...</span>
              </div>
            </div>
          )}

          {/* Chart Header & Integrated Legend */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-base text-foreground">Net Payable Forecast Stream</h3>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-mono bg-muted font-bold text-muted-foreground uppercase">
                  {results.metadata.granularity} cadence
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <span>Top Accuracy Model:</span>
                <span className="text-primary-600 font-mono font-bold bg-primary-50 px-1.5 py-0.2 rounded border border-primary-200">
                  {bestModel}
                </span>
              </p>
            </div>

            {/* Clean Legend Badges */}
            <div className="flex items-center gap-3 text-xs font-semibold flex-wrap">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 transition-all">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-500" />
                <span>Historical Baseline</span>
              </div>
              {showForecast && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60 animate-fade-in transition-all">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-pulse" />
                  <span>{bestModel} Forecast Horizon</span>
                </div>
              )}
            </div>
          </div>

          {/* Forecast Area Chart */}
          <div className="h-[400px] w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 15, right: 20, left: 10, bottom: 5 }}>
                <defs>
                  <linearGradient id="baselineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#64748b" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.0} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" opacity={0.7} />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  minTickGap={40}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(val) => `$${(val / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomForecastTooltip />} />

                {/* Historical Baseline Curve with subtle Area Fill & Smooth Interpolation */}
                <Area
                  type="linear"
                  dataKey="actual"
                  name="Observed Actuals"
                  stroke="#475569"
                  strokeWidth={2.2}
                  fill="url(#baselineGradient)"
                  dot={false}
                  activeDot={{ r: 5, strokeWidth: 0, fill: '#334155' }}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="ease-in-out"
                />

                {/* ML Forecast Stream with Smooth Animation */}
                {showForecast && (
                  <Line
                    type="linear"
                    dataKey={`${bestModel}_pred`}
                    name={`Forecast (${bestModel})`}
                    stroke="#2563eb"
                    strokeWidth={2.5}
                    strokeDasharray="4 4"
                    dot={{ r: 3, fill: '#2563eb', strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: '#ffffff', fill: '#1d4ed8' }}
                    connectNulls={true}
                    isAnimationActive={true}
                    animationDuration={800}
                    animationEasing="ease-in-out"
                  />
                )}

                {/* Horizon Start Reference Marker */}
                {cutoffDate && (
                  <ReferenceLine
                    x={cutoffDate}
                    stroke="#0284c7"
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                    isFront={true}
                    label={{
                      position: 'insideTopRight',
                      value: '• Horizon Start',
                      fill: '#0284c7',
                      fontSize: 11,
                      fontWeight: '700',
                    }}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Model Meta Bento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 pt-4 border-t border-border">
            <div className="bg-muted/35 p-4 rounded-xl border border-border/60 flex items-center gap-3 transition-transform duration-200 hover:scale-[1.02]">
              <div className="w-10 h-10 rounded-lg bg-slate-200 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 shrink-0">
                <Activity className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Series Duration</p>
                <p className="text-lg font-black font-mono text-foreground">
                  {results.metadata.series_length}{' '}
                  <span className="text-xs font-semibold text-muted-foreground">{results.metadata.granularity}s</span>
                </p>
              </div>
            </div>

            <div className="bg-muted/35 p-4 rounded-xl border border-border/60 flex items-center gap-3 transition-transform duration-200 hover:scale-[1.02]">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-950/60 flex items-center justify-center text-primary-600 dark:text-primary-400 shrink-0">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Memory Look-Back</p>
                <p className="text-lg font-black font-mono text-primary-600">
                  {results.metadata.look_back}{' '}
                  <span className="text-xs font-semibold text-muted-foreground">Periods</span>
                </p>
              </div>
            </div>

            <div className="bg-muted/35 p-4 rounded-xl border border-border/60 flex items-center gap-3 transition-transform duration-200 hover:scale-[1.02]">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-950/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Forecast Horizon</p>
                <p className="text-lg font-black font-mono text-indigo-600">
                  {results.metadata.forecast_horizon}{' '}
                  <span className="text-xs font-semibold text-muted-foreground">Steps Forward</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastingView;
